/**
 * bulkJobProgress.ts
 *
 * FIX-1311 (D3): the BatchCenter bulk-request progress poller used to
 * read status/progress fields directly off the
 * GET /api/admin/bulk-jobs/:jobId envelope. That endpoint answers
 * { success, job } — the fields live on the nested `job` object, so
 * the poller never saw progress move or the terminal state.
 *
 * These pure helpers read the real envelope shape; BatchCenter uses
 * them, and the render suite tests them against the real shape.
 */
export interface BulkJobProgressSnapshot {
  status: string;
  progressPercent: number;
  completedItems: number;
  failedItems: number;
  skippedItems: number;
  totalItems: number;
  errorSummary?: string;
}

/**
 * Extract the live progress snapshot from the raw
 * GET /api/admin/bulk-jobs/:jobId response ({ success, job }).
 * Returns null when the response carries no usable job object.
 */
export function extractBulkJobProgress(response: any): BulkJobProgressSnapshot | null {
  const job = response?.job;
  if (!job || typeof job !== 'object') return null;
  const completedItems = Number(job.completedItems) || 0;
  const failedItems = Number(job.failedItems) || 0;
  const skippedItems = Number(job.skippedItems) || 0;
  const totalItems = Number(job.totalItems) || 0;
  let progressPercent = Number(job.progressPercent);
  if (!Number.isFinite(progressPercent)) {
    progressPercent = totalItems > 0 ? Math.round(((completedItems + failedItems + skippedItems) / totalItems) * 100) : 0;
  }
  return {
    status: String(job.status || 'queued'),
    progressPercent: Math.max(0, Math.min(100, progressPercent)),
    completedItems,
    failedItems,
    skippedItems,
    totalItems,
    errorSummary: job.errorSummary || undefined,
  };
}

const TERMINAL_JOB_STATUSES = new Set(['completed', 'completed_with_errors', 'failed', 'cancelled']);

export function isBulkJobTerminalStatus(status: string): boolean {
  return TERMINAL_JOB_STATUSES.has(status);
}

/**
 * Build the one terminal-state toast for a bulk request send. Returns
 * null when the snapshot is not in a toasting terminal state (the
 * caller keeps its own once-guard per job id).
 */
export function buildBulkJobTerminalToast(
  progress: BulkJobProgressSnapshot,
  requestTitle: string,
): { type: 'success' | 'error'; message: string } | null {
  if (progress.status === 'completed') {
    return {
      type: 'success',
      message: `Bulk request "${requestTitle}" send completed (${progress.completedItems}/${progress.totalItems} sent)`,
    };
  }
  if (progress.status === 'completed_with_errors') {
    return {
      type: 'error',
      message: `Bulk request "${requestTitle}" completed with errors (${progress.completedItems} succeeded, ${progress.failedItems} failed)`,
    };
  }
  if (progress.status === 'failed') {
    return {
      type: 'error',
      message: `Bulk request "${requestTitle}" send failed: ${progress.errorSummary || 'Unknown error'}`,
    };
  }
  return null;
}
